import type { ReactNode, RefObject } from 'react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~/components/ui/alert-dialog';
import { buttonVariants } from '~/components/ui/button';
import { cn } from '~/utils/cn';

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  isPending?: boolean;
  /** Blocks only the confirm button (cancel stays usable) — for a confirm the caller knows is moot. */
  isConfirmDisabled?: boolean;
  onConfirm: () => void;
  /** When provided, the cancel button runs this action (instead of only
   *  closing) — e.g. an async side-effect. The dialog stays open while
   *  `isCancelPending` is true so the caller can navigate away on success. */
  onCancel?: () => void;
  /** Shows a pending state on the cancel button and blocks both buttons. */
  isCancelPending?: boolean;
  /** `data-testid` root for the dialog; buttons derive `${testId}-confirm-button` / `-cancel-button`. */
  testId?: string;
  /**
   * The control that opened the dialog. A dialog opened from state has no Radix trigger, so on
   * close focus would fall to the page; with this it returns to that control, as with a trigger.
   */
  returnFocusRef?: RefObject<HTMLElement | null>;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  isPending = false,
  isConfirmDisabled = false,
  onConfirm,
  onCancel,
  isCancelPending = false,
  testId = 'confirm-dialog',
  returnFocusRef,
}: ConfirmDialogProps) {
  const busy = isPending || isCancelPending;
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent
        className="p-0"
        data-testid={testId}
        onCloseAutoFocus={(event) => {
          const target = returnFocusRef?.current;
          if (!target?.isConnected) return;
          event.preventDefault();
          target.focus();
        }}
      >
        <AlertDialogHeader className="pt-7">
          <AlertDialogTitle
            className="text-center text-xl font-semibold"
            data-testid={`${testId}-title`}
          >
            {title}
          </AlertDialogTitle>
          {/* Long bodies (options, notes) scroll inside the dialog instead of off-screen. */}
          <AlertDialogDescription
            asChild
            className="text-md max-h-[60vh] overflow-y-auto px-4 text-center font-light"
          >
            <div>{description}</div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="border-t px-6 py-4">
          <AlertDialogCancel
            disabled={busy}
            data-testid={`${testId}-cancel-button`}
            {...(onCancel
              ? {
                  onClick: (e) => {
                    // Keep the dialog open for the async action; the caller
                    // navigates away on success.
                    e.preventDefault();
                    onCancel();
                  },
                }
              : {})}
          >
            {isCancelPending ? `${cancelLabel}...` : cancelLabel}
          </AlertDialogCancel>
          <AlertDialogAction
            className={cn(
              buttonVariants({ variant: 'destructive' }),
              'cursor-pointer bg-amber-500 text-white hover:bg-amber-600 focus-visible:ring-amber-500 disabled:cursor-not-allowed',
            )}
            disabled={busy || isConfirmDisabled}
            data-testid={`${testId}-confirm-button`}
            onClick={(e) => {
              e.preventDefault();
              onConfirm();
            }}
          >
            {isPending ? `${confirmLabel}...` : confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
